import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

interface GameEvent {
  id: string;
  type: 'player_joined' | 'player_submitted' | 'player_left' | 'round_complete' | 'close_call';
  playerName?: string;
  message: string;
  timestamp: number;
}

interface LiveGameFeedProps {
  roomId: string;
  currentRound: number;
}

export default function LiveGameFeed({ roomId, currentRound }: LiveGameFeedProps) {
  const [events, setEvents] = useState<GameEvent[]>([]);

  // Subscribe to player changes
  useEffect(() => {
    const channel = supabase
      .channel('player-activity-feed')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'round_selections',
          filter: `room_id=eq.${roomId}`
        },
        async (payload) => {
          const selection = payload.new as any;
          
          const { data: player } = await supabase
            .from('players')
            .select('name')
            .eq('id', selection.player_id)
            .single();
          
          if (player) {
            addEvent({
              type: 'player_submitted',
              playerName: player.name,
              message: `${player.name} made their choice`,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_history',
          filter: `room_id=eq.${roomId}`
        },
        (payload) => {
          const history = payload.new as any;
          addEvent({
            type: 'round_complete',
            message: `Round ${history.round_number} complete!`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId]);

  const addEvent = (event: Omit<GameEvent, 'id' | 'timestamp'>) => {
    const newEvent: GameEvent = {
      ...event,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
    };
    
    setEvents(prev => [...prev.slice(-4), newEvent]);
    
    setTimeout(() => {
      setEvents(prev => prev.filter(e => e.id !== newEvent.id));
    }, 5000);
  };

  const getEventIcon = (type: GameEvent['type']) => {
    switch (type) {
      case 'player_submitted': return '✓';
      case 'round_complete': return '🎉';
      case 'player_joined': return '●';
      case 'player_left': return '○';
      case 'close_call': return '🔥';
      default: return '•';
    }
  };

  const getEventColor = (type: GameEvent['type']) => {
    switch (type) {
      case 'player_submitted': return 'text-primary';
      case 'round_complete': return 'text-chaos-green';
      case 'player_joined': return 'text-chaos-green';
      case 'player_left': return 'text-muted-foreground';
      case 'close_call': return 'text-chaos-orange';
      default: return 'text-muted-foreground';
    }
  };

  if (events.length === 0) return null;

  // Hidden on mobile, visible on larger screens
  return (
    <motion.div 
      className="hidden sm:block fixed bottom-4 left-4 z-30 w-56 max-h-40 overflow-hidden"
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
    >
      <div className="glass-card rounded-lg p-2 space-y-1">
        <p className="text-xs text-muted-foreground font-bold flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-chaos-red animate-pulse" />
          LIVE
        </p>
        
        <AnimatePresence mode="popLayout">
          {events.map((event) => (
            <motion.div
              key={event.id}
              initial={{ x: -20, opacity: 0, height: 0 }}
              animate={{ x: 0, opacity: 1, height: 'auto' }}
              exit={{ x: -20, opacity: 0, height: 0 }}
              className="text-xs bg-secondary/50 rounded px-2 py-1"
            >
              <span className={getEventColor(event.type)}>
                {getEventIcon(event.type)}
              </span>
              <span className="ml-1 text-foreground">{event.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
